import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Trash2, Users, UserPlus, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const PropertyProviders = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [propertyProviders, setPropertyProviders] = useState([]);
  const [allMyProviders, setAllMyProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('new');
  const [selectedExistingProvider, setSelectedExistingProvider] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    type: '',
    contact: '',
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [propertyRes, propertyProvidersRes, allProvidersRes] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/properties/${id}/providers`),
        api.get('/providers'),
      ]);
      setProperty(propertyRes.data);
      setPropertyProviders(propertyProvidersRes.data);
      setAllMyProviders(allProvidersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNew = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${id}/providers`, formData);
      toast.success('Prestataire créé et ajouté au logement !');
      setDialogOpen(false);
      setFormData({ nom: '', type: '', contact: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleLinkExisting = async () => {
    if (!selectedExistingProvider) {
      toast.error('Sélectionnez un prestataire');
      return;
    }
    try {
      await api.post(`/properties/${id}/providers/${selectedExistingProvider}`);
      toast.success('Prestataire ajouté au logement !');
      setDialogOpen(false);
      setSelectedExistingProvider('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleUnlink = async (providerId) => {
    if (!window.confirm('Dissocier ce prestataire de ce logement ? (Il restera dans votre liste de prestataires)')) return;
    try {
      await api.delete(`/properties/${id}/providers/${providerId}`);
      toast.success('Prestataire dissocié du logement');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la dissociation');
    }
  };

  // Prestataires disponibles (non encore associés à ce logement)
  const availableProviders = allMyProviders.filter(
    p => !propertyProviders.some(pp => pp.id === p.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/properties')}
          data-testid="back-to-properties-btn"
          className="mb-4 sm:mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour
        </Button>

        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{property?.nom}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Gérez les prestataires</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-provider-btn" className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Ajouter un prestataire</DialogTitle>
              </DialogHeader>
              
              <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="new" className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Nouveau
                  </TabsTrigger>
                  <TabsTrigger value="existing" className="flex items-center gap-2" disabled={availableProviders.length === 0}>
                    <Link2 className="w-4 h-4" />
                    Existant ({availableProviders.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="new">
                  <form onSubmit={handleSubmitNew} className="space-y-4">
                    <div>
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        required
                        data-testid="provider-nom-input"
                        placeholder="Ex: Jean Dupont"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="type">Type *</Label>
                      <Input
                        id="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        required
                        data-testid="provider-type-input"
                        placeholder="Ex: Ménage, Maintenance, Linge..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact">Contact *</Label>
                      <Input
                        id="contact"
                        value={formData.contact}
                        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                        required
                        data-testid="provider-contact-input"
                        placeholder="Téléphone ou email"
                        className="mt-1"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Ce prestataire sera sauvegardé et réutilisable sur vos autres logements.
                    </p>
                    <Button type="submit" data-testid="provider-submit-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Créer et ajouter
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="existing">
                  <div className="space-y-4">
                    {availableProviders.length === 0 ? (
                      <div className="text-center py-6 text-slate-500">
                        <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>Tous vos prestataires sont déjà associés à ce logement</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label>Sélectionner un prestataire</Label>
                          <Select value={selectedExistingProvider} onValueChange={setSelectedExistingProvider}>
                            <SelectTrigger className="mt-1" data-testid="select-existing-provider">
                              <SelectValue placeholder="Choisir un prestataire..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProviders.map(provider => (
                                <SelectItem key={provider.id} value={provider.id}>
                                  {provider.nom} ({provider.type})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          onClick={handleLinkExisting} 
                          className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
                          disabled={!selectedExistingProvider}
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          Ajouter au logement
                        </Button>
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>

        {propertyProviders.length === 0 ? (
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-8 sm:p-12 text-center" data-testid="empty-providers-state">
            <Users className="w-12 sm:w-16 h-12 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" strokeWidth={1.5} />
            <h3 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aucun prestataire</h3>
            <p className="text-sm text-slate-500 mb-4 sm:mb-6">Ajoutez des prestataires pour ce logement</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Ajouter un prestataire
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="providers-grid">
            {propertyProviders.map((provider) => (
              <Card key={provider.id} className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6 hover-lift" data-testid={`provider-card-${provider.id}`}>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg lg:text-xl font-medium text-slate-900 mb-1 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{provider.nom}</h3>
                      <span className="inline-block px-2 py-1 bg-rose-50 text-rose-600 text-xs rounded">
                        {provider.type}
                      </span>
                    </div>
                  </div>

                  <div className="p-2 sm:p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Contact</p>
                    <p className="text-sm font-medium text-slate-900 truncate">{provider.contact}</p>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleUnlink(provider.id)}
                    data-testid={`unlink-provider-${provider.id}`}
                    className="w-full text-slate-600 hover:text-slate-700 hover:bg-slate-100 text-xs sm:text-sm"
                  >
                    <Unlink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Dissocier
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-rose-50 border border-rose-200 rounded-lg">
          <p className="text-xs sm:text-sm text-rose-800">
            <strong>Astuce :</strong> Les prestataires créés sont réutilisables sur vos autres logements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PropertyProviders;
